import organizationService, {
  respondToInvitationParams,
} from "./organization.service";
import {
  GetOrganizationsDto,
  CreateOrganizationMemberDto,
  FullOrganizationDto,
  OrganizationDto,
  OrganizationSelectionDto,
  UpdateOrganizationDto,
  GetOrganizationDto,
  UpdateOrganizationMemberDto,
  InviteCheckStatus,
} from "./organization.types";
import { GroupBranchDto } from "./group.types";
import { MemberStatus, PublicUserDto } from "../users/users.types";
import { createGroupTree } from "../../helpers/treeHelpers";
import { createThunkWithError } from "../featureHelpers/errorParser";
import { partition } from "../../helpers/helpers";

// createOrganization — includes post-processing (group tree)
export const createOrganization = createThunkWithError<OrganizationDto, string>(
  "organizations/create",
  async (organizationName) => {
    if (!organizationName) throw new Error("No organization name");
    const org = await organizationService.createOrganization(organizationName);
    const groupBranch = createGroupTree(org) as GroupBranchDto;
    return {
      ...org,
      groupBranch: { ...groupBranch, selectionState: 2 },
      organizationMembers: org.organizationMembers,
    };
  },
);

// Whether an email can be invited to the organization
export const checkInvitation = createThunkWithError<
  InviteCheckStatus,
  { organizationId: string; email: string }
>("organizations/checkInvitation", async (dto) =>
  organizationService.checkInvitation(dto.organizationId, dto.email),
);

export const createOrganizationMember = createThunkWithError<
  string,
  CreateOrganizationMemberDto
>("organizations/createOrganizationMember", ({ userId, organizationId }) =>
  organizationService.createOrganizationMember(userId, organizationId),
);

export const getOrganization = createThunkWithError<
  FullOrganizationDto,
  string
>("organizations/getSingle", async (organizationId) => {
  const orga = await organizationService.getOrganization(organizationId);
  const groupBranch = createGroupTree(orga) as GroupBranchDto;
  return {
    ...orga,
    groupBranch,
    organizationMembers: orga.organizationMembers,
  };
});

interface GetPartinitionedOrganizationsDto {
  memberOrganizationsMap: OrganizationSelectionDto[];
  pendingOrgs?: OrganizationSelectionDto[];
}

export const getOrganizations = createThunkWithError<
  GetPartinitionedOrganizationsDto,
  void
>("organizations/getMany", async () => {
  const responseData = await organizationService.getOrganizations();
  const [memberOrganizations, pendingOrganizations] = partition(
    responseData,
    (orga) => orga.status === "ACCEPTED",
  );

  const memberOrganizationsMap = memberOrganizations.map(
    (orga: GetOrganizationsDto) => {
      const groupBranch = createGroupTree(orga) as GroupBranchDto;
      return { ...orga, groupBranch, groups: undefined };
    },
  );

  const pendingOrganizationsMap = pendingOrganizations.map(
    (orga: GetOrganizationsDto) => {
      const groupBranch = createGroupTree(orga) as GroupBranchDto;
      return { ...orga, groupBranch, groups: undefined };
    },
  );

  return {
    memberOrganizationsMap: memberOrganizationsMap,
    pendingOrgs: pendingOrganizationsMap,
  };
});

export const updateOrganization = createThunkWithError<
  GetOrganizationDto,
  UpdateOrganizationDto
>("organizations/update", (organizationData) =>
  organizationService.updateOrganization(organizationData),
);

export const deleteOrganization = createThunkWithError<string, string>(
  "organizations/delete",
  async (organizationId) => {
    await organizationService.deleteOrganization(organizationId);
    return organizationId;
  },
);

export const removeOrganizationMember = createThunkWithError<
  { userId: string; organizationId: string },
  { userId: string; organizationId: string }
>(
  "organizations/removeOrganizationMember",
  async ({ userId, organizationId }) => {
    await organizationService.removeOrganizationMember(organizationId, userId);
    return { userId, organizationId };
  },
);

export const respondToInvitation = createThunkWithError<
  MemberStatus,
  respondToInvitationParams
>("organizations/respondToInvitation", async (updateData) => {
  await organizationService.respondToInvitation(updateData);
  return updateData.status;
});

export const updateOrganizationMember = createThunkWithError<
  PublicUserDto,
  UpdateOrganizationMemberDto
>("organizations/updateOrganizationMember", (updateData) =>
  organizationService.updateOrganizationMember(updateData),
);

export const leaveOrganization = createThunkWithError<
  string,
  { organizationId: string }
>("organizations/leaveOrganization", async ({ organizationId }) => {
  await organizationService.leaveOrganization(organizationId);
  return organizationId;
});
